import React, { useEffect, useState } from "react";
import CashEntryServices from "../../../services/CashEntryServices";
import Swal from "sweetalert2";
import Loader from "../Loader/loader";
import Pagination from "../Pagenation/Pagenation";

const getId = (value) =>
  String(value?._id || value?.value || value || "").trim();

const readEntries = (response) => {
  const body = response?.data?.status
    ? response.data
    : response;

  const entries = body?.data?.entries;

  if (!Array.isArray(entries)) {
    throw new Error("Unexpected rejected entries response.");
  }

  return entries;
};

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const CashRejectedEntriesPanel = ({
  storeId,
  passbookId,
  entryType = "in",
  onProjectionRefresh
}) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [acceptingId, setAcceptingId] = useState("");


  const acceptRejection = async (entry) => {
    if (acceptingId || entry.rejectionAccepted) return;

    // Lock actions while confirmation/request is open.
    setAcceptingId(entry._id);

    let accepted = false;

    try {
      const confirmation = await Swal.fire({
        icon: "warning",
        title: "Accept Rejection?",
        text:
          `Accept rejection for ${entry.requestNumber || "this entry"}? ` +
          "Its amount will be excluded from projection. " +
          "The entry will remain rejected.",
        showCancelButton: true,
        confirmButtonText: "Yes, Accept Rejection",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#198754",
        cancelButtonColor: "#6c757d",
        reverseButtons: true,
        focusCancel: true
      });

      if (!confirmation.isConfirmed) return;

      Swal.fire({
        title: "Accepting Rejection...",
        text: "Please wait.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      await CashEntryServices.acceptRejection(entry._id);

      accepted = true;
      setRefreshKey((previous) => previous + 1);

      await Swal.fire({
        icon: "success",
        title: "Rejection Accepted",
        text: "This amount is now excluded from projection.",
        confirmButtonColor: "#198754"
      });
    } catch (acceptError) {
      await Swal.fire({
        icon: "error",
        title: "Unable to Accept Rejection",
        text:
          acceptError?.response?.data?.message ||
          acceptError?.message ||
          "Please try again.",
        confirmButtonColor: "#dc3545"
      });
    } finally {
      setAcceptingId("");
    }

    if (accepted && onProjectionRefresh) {
      try {
        await onProjectionRefresh();
      } catch {
        await Swal.fire({
          icon: "warning",
          title: "Refresh Required",
          text:
            "Rejection was accepted successfully, but the summary " +
            "could not refresh. Please refresh the passbook viewer.",
          confirmButtonColor: "#0d6efd"
        });
      }
    }
  };

  const selectedStoreId = getId(storeId);
  const selectedPassbookId = getId(passbookId);

  const [activePage, setActivePage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const totalPages = Math.max(
    1,
    Math.ceil(entries.length / pageSize)
  );

  const currentPage = Math.min(activePage, totalPages - 1);
  const startIndex = currentPage * pageSize;

  const paginatedEntries = entries.slice(
    startIndex,
    startIndex + pageSize
  );

  useEffect(() => {
    setActivePage(0);
  }, [selectedStoreId, selectedPassbookId, entryType, pageSize]);

  useEffect(() => {
    let cancelled = false;

    setEntries([]);

    if (!selectedStoreId || !selectedPassbookId) {
      setLoading(false);
      return;
    }

    const loadEntries = async () => {
      setLoading(true);

      try {
        const statuses = entryType === "out"
          ? ["rejectedByAdmin", "rejectedByAccounts"]
          : ["rejectedByAccounts"];

        const responses = await Promise.all(
          statuses.map((status) =>
            CashEntryServices.getCashEntries({
              storeId: selectedStoreId,
              passbookId: selectedPassbookId,
              entryType,
              status,
              pendingOnly: false
            })
          )
        );

        const rejectedEntries = responses
          .flatMap(readEntries)
          .filter(
            (entry) =>
              statuses.includes(entry.status) &&
              entry.entryType === entryType &&
              getId(entry.store) === selectedStoreId &&
              getId(entry.passbook) === selectedPassbookId
          )
          .sort((first, second) => {
            const getRejectedTime = (entry) =>
              new Date(
                entry.status === "rejectedByAdmin"
                  ? entry.adminReviewedAt || entry.updatedAt
                  : entry.accountsReviewedAt || entry.updatedAt
              ).getTime() || 0;

            return getRejectedTime(second) - getRejectedTime(first);
          });

        if (!cancelled) {
          setEntries(rejectedEntries);
        }
      } catch (loadError) {
        if (!cancelled) {
          Swal.fire({
            icon: "error",
            title: "Unable to Load Rejected Entries",
            text:
              loadError?.response?.data?.message ||
              loadError?.message ||
              "Please try again.",
            confirmButtonColor: "#dc3545"
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadEntries();

    return () => {
      cancelled = true;
    };
  }, [
    selectedStoreId,
    selectedPassbookId,
    entryType,
    refreshKey
  ]);

  if (!selectedStoreId || !selectedPassbookId) {
    return (
      <div className="alert alert-info mb-0">
        Select a Store / Location and passbook to view
        rejected entries.
      </div>
    );
  }

  return (
    <div className="border rounded p-3 bg-white">
      <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
        <h6 className="mb-0">
          Rejected Cash {entryType === "in" ? "In" : "Out"}
          {!loading && ` (${entries.length})`}
        </h6>

        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          disabled={loading || Boolean(acceptingId)}
          onClick={() => setRefreshKey((previous) => previous + 1)}
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {loading ? (
        <div className="py-4" role="status" aria-label="Loading rejected entries">
          <Loader />
        </div>
      ) : entries.length === 0 ? (
        <div className="alert alert-info mb-0">
          No rejected Cash {entryType === "in" ? "In" : "Out"} entries found.
        </div>
      ) : (
        <div>
          <div className="d-flex align-items-center gap-2 mb-3">
            <label
              htmlFor={`rejected-page-size-${entryType}`}
              className="mb-0 small"
            >
              Show
            </label>

            <select
              id={`rejected-page-size-${entryType}`}
              className="form-control"
              value={pageSize}
              disabled={Boolean(acceptingId)}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setActivePage(0);
              }}
              style={{ width: 80 }}
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>

            <span className="small">entries</span>
          </div>

          <div className="table-responsive">
            <table className="table table-bordered table-striped align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Request No.</th>
                  <th>Entry Date</th>
                  <th>Category</th>
                  <th className="text-end">Amount (₹)</th>
                  <th>Remark</th>
                  <th>Rejected By</th>
                  <th>Rejected At</th>
                  <th>Rejection Reason</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {paginatedEntries.map((entry, index) => {
                  const byChecker =
                    entry.status === "rejectedByAdmin";

                  const reviewer = byChecker
                    ? entry.adminReviewedBy
                    : entry.accountsReviewedBy;

                  const reviewedAt = byChecker
                    ? entry.adminReviewedAt
                    : entry.accountsReviewedAt;

                  const reason = byChecker
                    ? entry.adminRemark
                    : entry.accountsRemark;

                  return (
                    <tr key={entry._id}>
                      <td>{startIndex + index + 1}</td>

                      <td className="text-nowrap">
                        <div>{entry.requestNumber || "—"}</div>
                        <div className="d-flex flex-column align-items-start gap-1 mt-1">
                          <span className="badge bg-danger">
                            Rejected
                          </span>

                          <span
                            className={`badge ${entry.rejectionAccepted === true
                                ? "bg-secondary"
                                : "bg-warning text-dark"
                              }`}
                          >
                            {entry.rejectionAccepted === true
                              ? "Rejection Accepted"
                              : "Awaiting Acceptance"}
                          </span>
                        </div>
                      </td>

                      <td className="text-nowrap">
                        {formatDate(entry.dateTime)}
                      </td>

                      <td>{entry.category?.name || "—"}</td>

                      <td className="text-end text-nowrap">
                        {Number(entry.amount || 0).toLocaleString(
                          "en-IN",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          }
                        )}
                      </td>

                      <td style={{ whiteSpace: "pre-wrap" }}>
                        {entry.remark || "—"}
                      </td>

                      <td>
                        <div>
                          {byChecker ? "CashChecker" : "Accounts"}
                        </div>

                        {reviewer?.name && (
                          <div className="small text-muted">
                            {reviewer.name}
                          </div>
                        )}

                        {!byChecker && entry.checkerBypassed && (
                          <span className="badge bg-warning text-dark mt-1">
                            Checker Bypassed
                          </span>
                        )}
                      </td>

                      <td className="text-nowrap">
                        {formatDate(reviewedAt)}
                      </td>

                      <td
                        style={{
                          minWidth: 220,
                          whiteSpace: "pre-wrap",
                          overflowWrap: "anywhere"
                        }}
                      >
                        {reason || "—"}
                      </td>
                      <td className="text-center">
                        {entry.rejectionAccepted ? (
                          <div>
                            <span className="badge bg-success">
                              Rejection Accepted
                            </span>

                            <div className="small text-muted mt-1">
                              Excluded from projection
                            </div>

                            {entry.rejectionAcceptedAt && (
                              <div className="small text-muted mt-1">
                                {formatDate(entry.rejectionAcceptedAt)}
                              </div>
                            )}
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-outline-success btn-sm"
                            disabled={Boolean(acceptingId)}
                            onClick={() => acceptRejection(entry)}
                          >
                            {acceptingId === entry._id
                              ? "Accepting..."
                              : "Accept Rejection"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
            <div className="dataTables_info">
              Showing {startIndex + 1} to{" "}
              {Math.min(startIndex + pageSize, entries.length)} of{" "}
              {entries.length} entries
            </div>

            <Pagination
              activePage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                if (
                  acceptingId ||
                  !Number.isInteger(page) ||
                  page < 0 ||
                  page >= totalPages
                ) {
                  return;
                }

                setActivePage(page);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CashRejectedEntriesPanel;